import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import WishlistsApi from "@/store/wishlists/-api";

const emptyFeedFactory: () => CollectionFeed = () => ({ ids: [], meta: null });
const favProdFeed = "favorite-products";

@Module({
  name: "wishlists",
  stateFactory: true,
  namespaced: true,
})
export default class WishlistsStore extends VuexModule {
  wishes: WishMap = {};
  collections: CollectionMap = {};
  feeds: CollectionFeeds = {};
  loggedOutFavLtkProductId = (process.client ? localStorage.getItem("favorite-product") : "") ?? "";

  get collectionFeed() {
    return (feedName: string) => this.feeds[feedName] ?? emptyFeedFactory();
  }

  get getFeedHasMorePages() {
    return (feedName: string) => {
      const feed = this.collectionFeed(feedName);
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.next);
    };
  }

  get hasFeedBeenFetched() {
    return (feedName: string) => {
      const feeds = this.feeds[feedName];
      return feeds && feeds.meta !== null && feeds.ids.length > 0;
    };
  }

  get getWishIdByLtkProductId() {
    return (ltkProductId: string) => {
      const wish = Object.values(this.wishes).find((item) => item?.ltkProductId === ltkProductId);
      return wish?.id;
    };
  }

  /**
   * Normally calling 1 mutation for every 1 model would be discouraged, however, the underlying
   * API call has a special debounce and argument aggregation decorator on it. Because of this,
   * multiple rapid calls to the API will be aggregated into a single call, resulting with only
   * the last call returning results, and the earlier ones having a rejected/cancelled promise.
   */
  @Action({ rawError: true })
  async fetchWishById(ltkProductId: string) {
    // Skip refreshing recent models
    if (!isModelStale(this.wishes[ltkProductId])) {
      return;
    }

    try {
      const api = new WishlistsApi(this.store.$axios);
      const response = await api.getOneWishByLtkProductId(ltkProductId);

      if (response?.data?.wishes?.length) {
        await processCoreModelResponse(this.store, response.data);
      }
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (error === null) {
        return undefined;
      }
    }
  }

  @Action({ rawError: true })
  async fetchCollectionById({ id }: { id: string }) {
    if (!isModelStale(this.collections[id])) {
      return;
    }

    const api = new WishlistsApi(this.store.$axios);
    const result = await api.getCollectionById({ id });

    const collection = result.data.wishCollection;
    collection.fetchedAt = Date.now();

    this.setCollection({ hash: id, collection });
  }

  @Action({ rawError: true })
  async fetchCollectionWishes({ id, overwrite = false }: { id: string; overwrite?: boolean }) {
    const { meta } = this.collectionFeed(id);

    if (!this.getFeedHasMorePages(id) && overwrite === false) {
      return;
    }

    const data: Parameters<WishlistsApi["getCollectionWishes"]>[0] = {};

    if (overwrite === false && meta?.next !== undefined) {
      data.cursor = meta.next;
    }

    const api = new WishlistsApi(this.store.$axios);

    data.id = id;

    const response = await api.getCollectionWishes(data);

    if (response.data.wishes.length > 0) {
      await processCoreModelResponse(this.store, response.data);
      const ids = response.data.wishes.map(({ id }) => id);

      this.setCollectionWishes({ feedName: id, ids, meta: response.data.meta, overwrite });
    }
  }

  @Action({ rawError: true })
  async fetchMyWishes({ overwrite = false }: { overwrite?: boolean }) {
    const { meta } = this.collectionFeed(favProdFeed);

    if (!this.getFeedHasMorePages(favProdFeed) && overwrite === false) {
      return;
    }

    const data: Parameters<WishlistsApi["getMyWishes"]>[0] = {};

    if (overwrite === false && meta?.next !== undefined) {
      data.cursor = meta.next;
    }

    const api = new WishlistsApi(this.store.$axios);

    const response = await api.getMyWishes(data);
    const wishes = response.data.wishes;

    if (wishes?.length > 0) {
      await processCoreModelResponse(this.store, response.data, true);

      const ids = wishes.map(({ id }) => id);
      this.setMyWishes({ ids, meta: response.data.meta, overwrite });
    }
  }

  @Action({ rawError: true })
  async addWish({ ltkProductId }: { ltkProductId: string }) {
    const api = new WishlistsApi(this.store.$axios);
    const response = await api.addWish({ ltkProductId });
    const { wish } = response.data;

    this.createWish({ wish });
    this.setMyWishes({ ids: [wish.id] });
  }

  @Action({ rawError: true })
  async removeWish({ ltkProductId }: { ltkProductId: string }) {
    const api = new WishlistsApi(this.store.$axios);
    const id = this.getWishIdByLtkProductId(ltkProductId) ?? "";

    await api.removeWish(id);

    this.deleteWish(id);
  }

  @Mutation
  setWishes({ wishes }: { wishes: WishMap }) {
    Vue.set(this, "wishes", Object.assign({}, this.wishes, wishes));
  }

  @Mutation
  setMyWishes({
    ids,
    meta = null,
    overwrite = false,
  }: {
    ids: string[];
    meta?: WishlistMeta | null;
    overwrite?: boolean;
  }) {
    if (this.feeds[favProdFeed] === undefined) {
      Vue.set(this.feeds, favProdFeed, { ids: [], meta: null });
    }

    // Add items to feed
    const start = overwrite ? 0 : this.feeds[favProdFeed].ids.length;
    const length = overwrite ? this.feeds[favProdFeed].ids.length : 0;
    this.feeds[favProdFeed].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[favProdFeed], "meta", meta ?? null);
  }

  @Mutation
  createWish({ wish }: { wish: Wish }) {
    Vue.set(this.wishes, wish.id, wish);
  }

  @Mutation
  deleteWish(wishId: string) {
    if (this.feeds[favProdFeed]?.ids?.length) {
      const idIndex = this.feeds[favProdFeed]?.ids?.findIndex((id) => id === wishId);

      if (idIndex !== -1) {
        this.feeds[favProdFeed].ids.splice(idIndex, 1);

        if (this.feeds[favProdFeed].ids.length === 0) {
          Vue.delete(this.feeds, favProdFeed);
        }
      }
    }

    Vue.delete(this.wishes, wishId);
  }

  @Mutation
  setCollection({ hash, collection }: { hash: string; collection: Collection }) {
    Vue.set(this.collections, hash, collection);
  }

  @Mutation
  setCollectionWishes({
    feedName,
    ids,
    meta,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: WishlistMeta;
    overwrite?: boolean;
  }) {
    if (this.feeds[feedName] === undefined) {
      Vue.set(this.feeds, feedName, emptyFeedFactory());
    }

    // Add items to feed
    const start = overwrite ? 0 : this.feeds[feedName].ids.length;
    const length = overwrite ? this.feeds[feedName].ids.length : 0;
    this.feeds[feedName].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[feedName], "meta", meta ?? null);
  }

  @Mutation
  setLoggedOutFavLtkProductId(ltkProductId: string) {
    this.loggedOutFavLtkProductId = ltkProductId;
  }

  @Mutation
  resetStore() {
    Vue.set(this, "wishes", {});
    Vue.set(this, "collections", {});
    Vue.set(this, "collectionFeeds", {});
  }
}

export const getWishlistsStore = (store: Store<unknown>) => {
  const wishlistsStore = getModule(WishlistsStore, store);

  return wishlistsStore;
};
